import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, Observable, Subject, from, of } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';
import { LoginDialogComponent } from '../components/broker/login-dialog/login-dialog.component';
import { META_API_ACCOUNT_KEY, MetaApiDownloadFase, MetaApiService } from './meta-api.service';
import { SseService } from './SseService';

export interface CompaniaMetaapi {
  iso: string;
  idNectum: number;
}

export interface SincronizarCallbacks {
  onBatch?: (batch: number, total: number) => void;
  onTotal?: (expected: number) => void;
  onFase?: (fase: MetaApiDownloadFase) => void;
}

/**
 * Descarga de velas MetaAPI para un activo y timeframe: el navegador pide las velas a MetaAPI
 * y las persiste en el backend (POST metaapi/candles, upsert).
 *
 * `sincronizar` es la misma lógica que CrearModeloComponent.downloadOneTimeframeForCompany.
 */
@Injectable({ providedIn: 'root' })
export class MetaapiCandlesSyncService {

  /** Emite cuando el broker termina de persistir velas nuevas: las gráficas refrescan sin esperar a su timer. */
  private velasSincronizadasSubject = new Subject<void>();
  readonly velasSincronizadas$ = this.velasSincronizadasSubject.asObservable();

  notificarVelasSincronizadas(): void {
    this.velasSincronizadasSubject.next();
  }

  /** Una gráfica acaba de abrirse y necesita las velas hasta ahora: el broker sincroniza sin esperar al cierre. */
  private sincronizacionSolicitadaSubject = new Subject<void>();
  readonly sincronizacionSolicitada$ = this.sincronizacionSolicitadaSubject.asObservable();

  solicitarSincronizacion(): void {
    this.sincronizacionSolicitadaSubject.next();
  }

  constructor(
    private metaApiService: MetaApiService,
    private sseService: SseService,
    private cookieService: CookieService,
    private toastr: ToastrService,
    private dialog: MatDialog,
  ) {}

  private get licencia(): string {
    return this.cookieService.get('auth_token');
  }

  /**
   * Devuelve el accountId listo para descargar, o null.
   * Con `interactivo`, si faltan credenciales avisa y abre el login de MetaAPI.
   */
  asegurarCredenciales(interactivo: boolean): Observable<string | null> {
    const cuenta = localStorage.getItem(META_API_ACCOUNT_KEY) || '';
    return this.metaApiService.canDownloadCandles(cuenta).pipe(
      concatMap(guard => {
        if (guard.ok) return of(guard.accountId);
        if (!interactivo) return of(null);
        this.toastr.error(guard.message, guard.title, { timeOut: 5000 });
        const ref = this.dialog.open(LoginDialogComponent, {
          width: '800px',
          height: '600px',
          data: { metaApi: true, soloLoginMetaApi: true, cuenta: guard.accountId || cuenta },
        });
        return ref.afterClosed().pipe(
          map((result: any) => result?.metaApiAuthenticated ? (result.accountId || this.metaApiService.getAccountId() || null) : null)
        );
      })
    );
  }

  /** Completa lo que falte de la serie (descarga inicial, huecos, cola antigua y tramo reciente). */
  sincronizar(compania: CompaniaMetaapi, timeframe: string, accountId: string, cb: SincronizarCallbacks = {}): Observable<any> {
    const licencia = this.licencia;
    const notifyFailure = (context: string) => (err: any) => {
      console.error(`[MetaAPI] ${context} (${compania.iso} ${timeframe})`, err);
      this.toastr.error(`Descarga incompleta de ${compania.iso} (${timeframe}). Se reintentará en la próxima sincronización.`, 'MetaAPI', { timeOut: 6000 });
      return EMPTY;
    };
    const onExhausted = (fechaConfirmada: string) => {
      this.sseService.apiPostNew('metaapi/historiaAgotada', { idNectum: compania.idNectum, timeframe, fechaConfirmada }, licencia).subscribe({
        error: (err: any) => console.error(`[MetaAPI] Error marcando historia agotada (${compania.iso} ${timeframe})`, err)
      });
    };
    const onHuecoExhausted = (huecoInferior: string, huecoSuperior: string) => {
      this.sseService.apiPostNew('metaapi/huecoConfirmado', { idNectum: compania.idNectum, timeframe, huecoInferior, huecoSuperior }, licencia).subscribe({
        error: (err: any) => console.error(`[MetaAPI] Error marcando hueco confirmado (${compania.iso} ${timeframe})`, err)
      });
    };
    const guardar = (candles: any[], contractSize?: number) => {
      if (!candles || candles.length === 0) return EMPTY;
      const body: any = { candles, symbol: compania.iso, timeframe, idNectum: compania.idNectum };
      if (contractSize != null) body.contractSize = contractSize;
      return this.sseService.apiPostNew('metaapi/candles', body, licencia) as Observable<any>;
    };

    return (this.sseService.apiPostNew('metaapi/tieneCandles', { idNectum: compania.idNectum, timeframe, accountId }, licencia) as Observable<any>).pipe(
      concatMap((res: any) => {
        if (res.tieneCandles) {
          return this.metaApiService.downloadMissingCandles(accountId, compania.iso, timeframe, res, cb.onBatch, cb.onTotal, cb.onFase, onExhausted, onHuecoExhausted).pipe(
            concatMap((candles: any[]) => guardar(candles)),
            catchError(notifyFailure('Error rellenando huecos de velas'))
          );
        }
        cb.onFase?.('inicial');
        const fromDate = MetaApiService.historicalFromDate(timeframe);
        return this.metaApiService.getSymbolSpecification(accountId, compania.iso).pipe(
          concatMap((spec: any) => {
            if (!spec) return EMPTY;
            return from([
              this.metaApiService.getCandlesHistorical(accountId, compania.iso, timeframe, fromDate, undefined, undefined, cb.onBatch, cb.onTotal, onExhausted).pipe(
                concatMap((batch: any[]) => guardar(batch, spec.contractSize)),
                catchError(notifyFailure('Error en descarga inicial de velas'))
              ),
              (this.sseService.apiPostNew('metaapi/specificationes', { ...spec, tipoBroker: 'METAAPI' }, licencia) as Observable<any>).pipe(
                catchError(notifyFailure('Error guardando especificación de símbolo'))
              )
            ]).pipe(concatMap(obs$ => obs$));
          }),
          catchError(notifyFailure('Error obteniendo especificación de símbolo'))
        );
      }),
      catchError(notifyFailure('Error verificando estado de velas'))
    );
  }

  /**
   * Persiste las últimas velas cerradas y devuelve la que está en formación, que no se guarda en
   * BD, como filas [ms, open, high, low, close, volumen] para pintarla en memoria. El ms sale del
   * brokerTime tratado como UTC, la misma base con la que el backend devuelve la serie, así que la
   * vela en formación cae en la hora que tendrá en BD cuando se cierre.
   * Se ejecuta en bucle, así que los errores se propagan sin toastr; quien llama decide qué hacer.
   */
  actualizarUltimas(compania: CompaniaMetaapi, timeframe: string, accountId: string, limit: number = 3): Observable<number[][]> {
    return this.metaApiService.getLatestCandles(accountId, compania.iso, timeframe, limit).pipe(
      concatMap(candles => {
        const cerradas = candles.filter(c => c?.state !== 'intermediate');
        const enFormacion: number[][] = [];
        for (const c of candles) {
          if (c?.state !== 'intermediate') continue;
          const ms = this.metaApiService.candleComparisonTime(c, timeframe);
          if (ms == null) continue;
          enFormacion.push([ms, Number(c.open), Number(c.high), Number(c.low), Number(c.close), Number(c.tickVolume ?? 0)]);
        }
        const guardar$ = cerradas.length
          ? this.sseService.apiPostNew('metaapi/candles', { candles: cerradas, symbol: compania.iso, timeframe, idNectum: compania.idNectum }, this.licencia) as Observable<any>
          : of(null);
        return guardar$.pipe(map(() => enFormacion));
      })
    );
  }
}
